/**
 * RestoreOrderCard — "un-cancel" een geannuleerde Wasserij-order.
 *
 * Verschijnt wanneer de order geannuleerd is met een andere reden dan
 * "Geen reactie op reminders" (die reden heeft zijn eigen OphaalReminderCard
 * met heractiveer-knop). Samen dekken beide kaarten alle geannuleerde orders,
 * zonder overlap.
 *
 * Knop roept POST /api/orders/reactivate aan → OfficeOrderManagementService →
 * WerkOrderReactivationService. Zet de order terug op 'Ophalen plannen' met een
 * verse reminder-sequence; annulering-velden worden gewist. Role-gate
 * (admin|medewerker) zit server-side; de mount-plek verbergt de kaart al voor
 * andere rollen.
 */

import { useState } from 'react';
import { reactivateOrder, type OrderDetail } from '../../lib/orders';

interface Props {
  order: OrderDetail;
  onRestored: () => void;
}

const REMINDER_CANCEL_REASON = 'Geen reactie op reminders';

export function RestoreOrderCard({ order, onRestored }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const redenAnnulering = order.reminderState?.redenAnnulering ?? null;
  const visible =
    order.status === 'Geannuleerd' && redenAnnulering !== REMINDER_CANCEL_REASON;
  if (!visible) return null;

  const handleRestore = async () => {
    setBusy(true);
    setError(null);
    try {
      // Geen expectedLastModifiedDate — zelfde keuze als CancelOrderDialog:
      // een geannuleerde order wordt zelden gelijktijdig bewerkt.
      await reactivateOrder(order.id);
      setShowConfirm(false);
      onRestored();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Herstellen mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Geannuleerde order
        </h2>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium border bg-slate-100 text-slate-700 border-slate-200">
          Geannuleerd
        </span>
      </div>

      <p className="text-sm text-slate-600">
        Per ongeluk geannuleerd? Herstel de order — hij gaat terug naar{' '}
        <code>Ophalen plannen</code> en de klant doorloopt opnieuw de normale
        ophaal-flow (verse reminder-sequence, eerste mail over ~2 dagen).
      </p>

      {redenAnnulering && (
        <p className="text-xs text-slate-500">
          Reden van annulering: <strong>{redenAnnulering}</strong>
        </p>
      )}

      <p className="text-xs text-slate-400">
        Let op: een eventuele annuleringsmail is al verstuurd en de Opportunity
        wordt niet automatisch heropend. Stuur de klant zo nodig zelf een
        bevestiging.
      </p>

      {error && (
        <div className="text-xs px-2 py-1.5 rounded bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      {!showConfirm ? (
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          disabled={busy}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Herstel order
        </button>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-200 bg-indigo-50 flex-wrap">
          <span className="text-sm text-slate-700">
            Weet je het zeker? De order gaat terug naar <code>Ophalen plannen</code>.
          </span>
          <button
            type="button"
            onClick={handleRestore}
            disabled={busy}
            className="px-2 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? 'Bezig…' : 'Ja, herstel order'}
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            disabled={busy}
            className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
          >
            Annuleer
          </button>
        </div>
      )}
    </div>
  );
}
